import {useState} from "react";
import moment from "moment";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ModalOverflowDelta from "site-apps/distribution/dynamic/ModalOverflowDelta";
import {CRITERIA_TITLES} from "./ModelsDynamic";

const CollectIcon = ({collect}) =>
    collect ? (
        <FontAwesomeIcon icon="circle" className="text-success" title="Recoger" />
    ) : (
        <FontAwesomeIcon icon="circle" className="text-danger" title="No recoger" />
    );

// Valor que se compara con el umbral de cada C: solo si se cumple (verde); si no, vacío
const CValue = ({value, on}) => {
    if (!on || value === null || value === undefined) return "";
    return <span className="text-success fw-bold">{value}%</span>;
};

// Etiqueta de cada C en las celdas Día +N: N = nivel, P50FR = P50 fin de ruta, P90MR = P90 en la próxima ruta
const DAY_LABELS = {c1: "N", c2: "P50FR", c3: "P90MR"};

// Primer criterio que hace recoger un día (backend: trigger) y su valor: "P50FR 74%", "4 días", "Forzado"
const triggerText = (trigger) => {
    switch (trigger.criterion) {
        case "force":
            return "Forzado";
        case "days":
            return `${trigger.value} días`;
        default:
            return `${DAY_LABELS[trigger.criterion]} ${trigger.value}%`;
    }
};

// Todos los criterios que se cumplen un día, en el orden de los motivos: "Forzado", "N 85%", "P90MR 92%", "4 días"
const metTexts = (day) => {
    const values = cValues(day);
    return [
        day.forced && triggerText({criterion: "force"}),
        ...C_NAMES.filter((name) => day[name]).map((name) => triggerText({criterion: name, value: values[name]})),
        day.days_reached && triggerText({criterion: "days", value: day.days_since_unload}),
    ].filter(Boolean);
};

// Celda de un Día +N: todos los criterios que se cumplen, uno por línea; vacía si no se cumple ninguno
const TriggerCell = ({day}) => (
    <td className="text-center text-nowrap" title={day?.reason}>
        {day &&
            metTexts(day).map((text) => (
                <div key={text} className="text-success fw-bold">
                    {text}
                </div>
            ))}
    </td>
);

// Día +N con todos sus criterios sobre el mismo día: C1/C2/forzado/días de forecast[N] y C3 del día anterior
// (forecast[N-1].tomorrow_p90 = nivel previsto en su próxima ruta)
const dayOf = (forecast, n) =>
    forecast[n] && {...forecast[n], c3: forecast[n - 1].c3, tomorrow_p90: forecast[n - 1].tomorrow_p90};

const C_NAMES = ["c1", "c2", "c3"];

// Valor comparado por cada C en un día: C1 nivel al empezar el día, C2 previsto fin de ruta (P50),
// C3 próxima ruta (P90)
const cValues = (day) => ({
    c1: day.level,
    c2: day.window_p50,
    c3: day.tomorrow_p90,
});

// Cabeceras C1/C2/C3 de un día, con su leyenda
const CHeaders = () =>
    C_NAMES.map((name) => (
        <th key={name} className="text-end" title={CRITERIA_TITLES[name]}>
            {name.toUpperCase()}
        </th>
    ));

// Celdas C1/C2/C3 de un día (forecast[n]); el motivo del día va en el title
const CCells = ({day}) => {
    const values = cValues(day);
    return C_NAMES.map((name) => (
        <td key={name} className="text-end" title={day.reason}>
            <CValue value={values[name]} on={day[name]} />
        </td>
    ));
};

// C4/C5/C6 de hoy: tramos superados (verde si llega a su umbral, gris si no) y horas sin comunicar
const NoDataValue = ({noData}) => {
    if (!noData) return "";
    return (
        <>
            {noData.tiers.map((tier) => (
                <span key={tier.criterion} className={tier.met ? "text-success fw-bold me-1" : "text-muted me-1"}>
                    {tier.criterion.toUpperCase()}
                </span>
            ))}
            <span className="text-muted">{Math.round(noData.hours)} h</span>
        </>
    );
};

// Número tal cual (para que se pueda sumar a simple vista); sin dato, "-"
const num = (value, suffix = "") => (value === null || value === undefined ? "-" : `${value}${suffix}`);

/*
 * Resultado de los cálculos de cada contenedor: nivel, ritmo de llenado (medio a 30 días y de hoy),
 * C1/C2/C3, último vaciado, días para llenado y si tocaría recoger los días siguientes.
 * items: respuesta de /api/distribution/dynamic/overflow/preview/ con el asset del filtro en `asset`.
 */
const TabPreviewForecastTable = ({items, days, routeDays}) => {
    // Días naturales (Día +N); los que coinciden con una ruta de la programación llevan debajo Ruta +k
    const columns = days.map((date, idx) => ({n: idx + 1, route: routeDays.indexOf(date) + 1}));
    // Contenedor del que se ve el delta diario: {uuid, title}; null con el modal cerrado
    const [delta, setDelta] = useState(null);

    return (
        <>
        <table className="table table-striped table-sm">
            <thead>
                <tr>
                    <th rowSpan={2}>Contenedor</th>
                    <th rowSpan={2} className="text-end">
                        Nivel
                    </th>
                    <th rowSpan={2} className="text-end" title="Variación diaria media 30 días">
                        VDM 30
                    </th>
                    <th rowSpan={2} className="text-end">
                        Ritmo de hoy
                    </th>
                    <th colSpan={4} className="text-center">
                        Hoy
                    </th>
                    <th rowSpan={2} className="text-end">Último vaciado</th>
                    <th rowSpan={2} className="text-end" title="Días para llegar al 100% al ritmo de hoy">
                        Lleno en
                    </th>
                    {columns.map(({n, route}) => (
                        <th key={n} rowSpan={2} className="text-center">
                            {route > 0 && <div>Ruta +{route}</div>}
                            <div className="small text-muted fw-normal">Día +{n}</div>
                        </th>
                    ))}
                </tr>
                <tr>
                    <CHeaders />
                    <th className="text-end" title="Tiempo sin comunicar (C4/C5/C6)">
                        Sin com.
                    </th>
                </tr>
            </thead>
            <tbody>
                {items.map((item) => {
                    const model = item.model || {};
                    const uid = item.asset?.uid || item.uid;
                    return (
                        <tr key={item.id}>
                            <td>
                                <div>
                                    <CollectIcon collect={item.collect} /> {uid}
                                    {model.uuid && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary py-0 px-1 ms-2"
                                            title="Ver delta diario"
                                            onClick={() => setDelta({uuid: model.uuid, title: uid})}>
                                            <FontAwesomeIcon icon="chart-line" />
                                        </button>
                                    )}
                                </div>
                                <div className="text-muted small">
                                    {item.asset?.island_id && `(${item.asset.island_name})`}
                                </div>
                            </td>
                            <td className="text-end">{num(item.filling, "%")}</td>
                            <td className="text-end">{num(model.baseline_rate, "%")}</td>
                            <td className="text-end">{num(model.rate_today, "%")}</td>
                            <CCells day={item.today} />
                            <td className="text-end" title={item.today?.reason}>
                                <NoDataValue noData={item.today?.no_data} />
                            </td>
                            <td className="text-end">{item.live?.last_unload_at ? moment(item.live.last_unload_at).format("DD-MM HH:mm") : "-"}</td>
                            <td className="text-end">{num(model.days_to_full, " d")}</td>
                            {columns.map(({n}) => (
                                <TriggerCell key={n} day={dayOf(item.forecast, n)} />
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </table>
        <ModalOverflowDelta show={!!delta} setShow={() => setDelta(null)} uuid={delta?.uuid} title={delta?.title} />
        </>
    );
};

export default TabPreviewForecastTable;
